/**
 * 增强预览面板
 * 支持原始版/编辑版切换、localStorage 持久化、Word 导出
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLanguage } from '../../i18nContext';
import {
  useEditorContext,
  useSelectedAbstractNumId,
  useGeneratedCSS,
  useCurrentLevelStyleId,
  useEditableMaxLevel,
} from '../../schema/hooks/useEditor';
import { computeAllLevelStyles } from '../../schema/core/styleResolver';
import { BlockEditorContainer } from './BlockEditor';
import { computeStyleConfig, computeBulletText, computeBlockLevelIndices, WORD_CONTENT_WIDTH_PX } from './BlockEditor/blockStyleUtils';
import type { BlockStyleConfig } from './BlockEditor/types';
import type { NoteBlock } from '../../types';
import { downloadWord } from '../../export/wordExporter';
import type { StructuredContentItem } from '../../export/wordExporter';
import { TutorialZone } from './TutorialZone';
import { getUserSchemas } from '../../services/editorSchema';
import type { EditorSchemaDTO } from '../../services/editorSchema';
import { getToken } from '../../services/api';
import { saveEditedNote } from '../../services/note';

// ============ localStorage 工具 ============

const EDITED_NOTES_PREFIX = 'minutes-edited-notes-';

function loadEditedNotes(taskId: string | null | undefined): NoteBlock[] | null {
  if (!taskId) return null;
  try {
    const saved = localStorage.getItem(EDITED_NOTES_PREFIX + taskId);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error('Failed to load edited notes:', e);
  }
  return null;
}

function saveEditedNotes(taskId: string | null | undefined, blocks: NoteBlock[]) {
  if (!taskId) return;
  try {
    localStorage.setItem(EDITED_NOTES_PREFIX + taskId, JSON.stringify(blocks));
  } catch (e) {
    console.error('Failed to save edited notes:', e);
  }
}

function clearEditedNotes(taskId: string | null | undefined) {
  if (!taskId) return;
  localStorage.removeItem(EDITED_NOTES_PREFIX + taskId);
}

// ============ 预览面板组件 ============

type ViewMode = 'original' | 'edited';

const LEVEL_LABELS_ZH = ['一', '二', '三', '四', '五', '六', '七', '八'];
const LEVEL_LABELS_EN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

interface EnhancedPreviewProps {
  meetingNotes?: NoteBlock[];
  originalNotes?: NoteBlock[];
  onNotesChange?: (notes: NoteBlock[]) => void;
  taskId?: string | null;
  activeLinkId?: string | null;
  linkedIds?: Set<string>;
  onLinkToggle?: (id: string) => void;
  isSchemaEditMode?: boolean;
  matchedNoteIds?: Set<string>;
  topLinkedId?: string | null;
  taskTitle?: string;
  showDemoText?: boolean;
  showOriginal?: boolean;
  /** 是否处于模板中心模式 */
  isTemplateCenter?: boolean;
  /** 切换模板中心/编辑纪要 */
  onToggleTemplateCenter?: () => void;
  /** 应用模板回调 */
  onApplyTemplate?: (type?: string, schemaId?: string) => void;
  /** 当前应用的模板ID */
  appliedSchemaId?: string;
  /** 后端保存的编辑版纪要 */
  savedEditedNotes?: NoteBlock[] | null;
}

export function EnhancedPreview({ meetingNotes, originalNotes, onNotesChange, taskId, activeLinkId, linkedIds, onLinkToggle, isSchemaEditMode, matchedNoteIds, topLinkedId, taskTitle, showDemoText, showOriginal, isTemplateCenter, onToggleTemplateCenter, onApplyTemplate, appliedSchemaId, savedEditedNotes }: EnhancedPreviewProps) {
  const { state } = useEditorContext();
  const css = useGeneratedCSS();
  const [selectedAbstractNumId] = useSelectedAbstractNumId();
  const [currentMaxLevel] = useEditableMaxLevel();
  const styleId = useCurrentLevelStyleId();
  const { t, language } = useLanguage();

  // 模板下拉框：加载用户模板列表
  const [userSchemas, setUserSchemas] = useState<EditorSchemaDTO[]>([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState(appliedSchemaId || '');

  useEffect(() => {
    if (!getToken()) return;
    getUserSchemas().then(res => {
      setUserSchemas(res || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (appliedSchemaId) setSelectedSchemaId(appliedSchemaId);
  }, [appliedSchemaId]);

  const handleSchemaChange = useCallback((schemaId: string) => {
    setSelectedSchemaId(schemaId);
    onApplyTemplate?.('user', schemaId);
  }, [onApplyTemplate]);

  // 计算所有级别的样式（传给 BlockEditorContainer）
  const levelStyles = useMemo(() => {
    try {
      return computeAllLevelStyles(state.schema, selectedAbstractNumId, currentMaxLevel);
    } catch {
      return [];
    }
  }, [state.schema, selectedAbstractNumId, currentMaxLevel]);

  // 原始版用的 styleConfig（无 gutter）
  const originalStyleConfigs = useMemo(() => {
    const configs: BlockStyleConfig[] = [];
    for (const ls of levelStyles) {
      configs[ls.level] = computeStyleConfig(ls);
    }
    return configs;
  }, [levelStyles]);

  const [parseError, setParseError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<string | null>(null);

  // 示例文字 blocks（模板样式预览用）
  const demoBlocks = useMemo<NoteBlock[]>(() => {
    const labels = language === 'zh' ? LEVEL_LABELS_ZH : LEVEL_LABELS_EN;
    const blocks: NoteBlock[] = [];
    for (let lvl = 1; lvl <= currentMaxLevel + 1; lvl++) {
      const label = labels[lvl - 1] || `${lvl}`;
      const text = language === 'zh' ? `${label}级文字示例` : `Level ${label} sample text`;
      blocks.push({ id: `demo-${lvl}-1`, level: lvl, content: text });
      blocks.push({ id: `demo-${lvl}-2`, level: lvl, content: text });
    }
    return blocks;
  }, [currentMaxLevel, language]);

  // 原始版/编辑版切换
  const hasSavedEdits = !!loadEditedNotes(taskId) || (savedEditedNotes && savedEditedNotes.length > 0);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const initial = hasSavedEdits ? 'edited' : 'original';
    try { localStorage.setItem('preview-view-mode', initial); } catch {}
    return initial;
  });

  // 用 ref 追踪是否是内部编辑导致的 meetingNotes 变化
  const isInternalEditRef = React.useRef(false);
  // 保存按钮反馈状态
  const [justSaved, setJustSaved] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 原始 blocks（从 meetingNotes 转换，不可变）
  const originalBlocks = useMemo<NoteBlock[]>(() => {
    if (meetingNotes && meetingNotes.length > 0) {
      return meetingNotes.map((note, i) => ({
        ...note,
        id: note.id || `block-init-${i}`,
      }));
    }
    return [];
  }, [meetingNotes]);

  // 稳定的原始 blocks：只在外部数据加载时更新
  // 优先使用 originalNotes（Workspace 独立维护，不受编辑影响）
  const [stableOriginal, setStableOriginal] = useState<NoteBlock[]>(originalBlocks);
  const stableOriginalInitRef = React.useRef(false);

  useEffect(() => {
    // 优先用 originalNotes（真正的原始版，不受编辑影响）
    if (originalNotes && originalNotes.length > 0) {
      setStableOriginal(originalNotes);
      stableOriginalInitRef.current = true;
      return;
    }
    // 只在首次或外部数据加载时从 originalBlocks 初始化
    if (!stableOriginalInitRef.current) {
      if (originalBlocks.length > 0) {
        setStableOriginal(originalBlocks);
        stableOriginalInitRef.current = true;
      }
    }
  }, [originalNotes, originalBlocks]);

  // 追踪是否已经从 localStorage 恢复过编辑数据
  const hasRestoredFromStorageRef = React.useRef(false);

  // 编辑版 blocks：优先从 localStorage 加载，其次后端编辑版，否则用原始版
  const [editedBlocks, setEditedBlocks] = useState<NoteBlock[]>(() => {
    const saved = loadEditedNotes(taskId);
    if (saved) { hasRestoredFromStorageRef.current = true; return saved; }
    if (savedEditedNotes && savedEditedNotes.length > 0) { hasRestoredFromStorageRef.current = true; return savedEditedNotes; }
    return originalBlocks;
  });

  // 编辑版的 stableBlocks（传给 BlockEditorContainer 的 initialBlocks）
  const [stableEdited, setStableEdited] = useState<NoteBlock[]>(() => {
    const saved = loadEditedNotes(taskId);
    if (saved) return saved;
    if (savedEditedNotes && savedEditedNotes.length > 0) return savedEditedNotes;
    return originalBlocks;
  });

  // 当 stableOriginal 变化时（外部数据加载），同步 editedBlocks 和 stableEdited
  useEffect(() => {
    // 如果已经从 localStorage 或后端恢复过，不再覆盖用户编辑
    if (hasRestoredFromStorageRef.current) return;

    const saved = loadEditedNotes(taskId);
    if (saved) {
      setEditedBlocks(saved);
      setStableEdited(saved);
      hasRestoredFromStorageRef.current = true;
      return;
    }
    if (savedEditedNotes && savedEditedNotes.length > 0) {
      setEditedBlocks(savedEditedNotes);
      setStableEdited(savedEditedNotes);
      hasRestoredFromStorageRef.current = true;
      return;
    }
    // 没有保存的编辑，用原始版同步
    setEditedBlocks(stableOriginal);
    setStableEdited(stableOriginal);
  }, [stableOriginal, taskId, savedEditedNotes]);

  // blocks 变化回调（从 BlockEditorContainer）
  const handleBlocksChange = useCallback((blocks: NoteBlock[]) => {
    setEditedBlocks(blocks);
    saveEditedNotes(taskId, blocks);
    if (onNotesChange) {
      isInternalEditRef.current = true;
      onNotesChange(blocks);
    }
  }, [onNotesChange, taskId]);

  // 手动保存
  const handleSave = useCallback(() => {
    saveEditedNotes(taskId, editedBlocks);
    if (onNotesChange) {
      isInternalEditRef.current = true;
      onNotesChange(editedBlocks);
    }
    // 保存到后端
    if (taskId && getToken()) {
      saveEditedNote(taskId, JSON.stringify(editedBlocks)).catch(err =>
        console.error('Failed to save edited note to backend:', err)
      );
    }
    const now = new Date();
    setLastSaveTime(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`);
    // 按钮反馈
    setJustSaved(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setJustSaved(false), 1500);
  }, [editedBlocks, onNotesChange, taskId]);

  // 每分钟定时保存
  useEffect(() => {
    if (viewMode !== 'edited') return;
    const timer = setInterval(() => {
      saveEditedNotes(taskId, editedBlocks);
      if (onNotesChange) {
        isInternalEditRef.current = true;
        onNotesChange(editedBlocks);
      }
      // 自动保存到后端
      if (taskId && getToken()) {
        saveEditedNote(taskId, JSON.stringify(editedBlocks)).catch(err =>
          console.error('Auto-save to backend failed:', err)
        );
      }
      const now = new Date();
      setLastSaveTime(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`);
    }, 60000);
    return () => clearInterval(timer);
  }, [viewMode, editedBlocks, onNotesChange, taskId]);

  // 当前显示的 blocks（用于 Word 导出）
  // 原始版始终使用 originalNotes（Workspace 独立维护，不受编辑影响）
  const realOriginal = (originalNotes && originalNotes.length > 0) ? originalNotes : stableOriginal;
  const currentBlocks = viewMode === 'original' ? realOriginal : editedBlocks;

  // 兼容 previewContent 用于 Word 导出
  const previewContent = useMemo<StructuredContentItem[]>(() => {
    return currentBlocks.map(b => ({ level: b.level, content: b.content }));
  }, [currentBlocks]);

  const scopeClass = `list-num-${selectedAbstractNumId}`;

  // 切换到原始版：先同步 stableEdited，确保切回编辑版时不丢数据
  const switchToOriginal = useCallback(() => {
    setStableEdited(editedBlocks);
    setViewMode('original');
    try { localStorage.setItem('preview-view-mode', 'original'); } catch {}
  }, [editedBlocks]);

  // 切换到编辑版：从 localStorage 恢复最新编辑
  const switchToEdited = useCallback(() => {
    const saved = loadEditedNotes(taskId);
    if (saved) {
      setEditedBlocks(saved);
      setStableEdited(saved);
    }
    setViewMode('edited');
    try { localStorage.setItem('preview-view-mode', 'edited'); } catch {}
  }, [taskId]);

  // 重置编辑版为原始版
  const handleReset = useCallback(() => {
    clearEditedNotes(taskId);
    // 创建新引用，确保 BlockEditorContainer 检测到变化并重新 INIT
    const freshBlocks = stableOriginal.map(b => ({ ...b }));
    setEditedBlocks(freshBlocks);
    setStableEdited(freshBlocks);
    setParseError(null);
  }, [stableOriginal, taskId]);

  // 导出当前内容为Word
  const handleExportToWord = useCallback(async () => {
    setIsExporting(true);
    try {
      const safeName = taskTitle
        ? taskTitle.replace(/[<>:"/\\|?*]/g, '_').trim()
        : 'document';
      await downloadWord(previewContent, state.schema, {
        abstractNumId: selectedAbstractNumId,
        inheritFromLevel: currentMaxLevel,
      }, `${safeName}.docx`);
    } catch (error) {
      console.error('Export error:', error);
      setParseError(`${t.workspace.exportFailed}: ${error}`);
    } finally {
      setIsExporting(false);
    }
  }, [previewContent, state.schema, selectedAbstractNumId, currentMaxLevel, taskTitle]);

  return (
    <div className="enhanced-preview" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {!isSchemaEditMode && (
      <div className="preview-header" style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        {/* 左侧：Schema文字 + 模板下拉框 + 模板中心切换按钮 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getToken() && userSchemas.length > 0 && (
            <>
              <span style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>{t.templateCenter.schema}</span>
              <select
              value={selectedSchemaId}
              onChange={e => handleSchemaChange(e.target.value)}
              style={{ fontSize: '13px', fontWeight: 500, maxWidth: 200, padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            >
              <option value="" hidden></option>
              {userSchemas.map(s => (
                <option key={s.schemaId} value={s.schemaId}>{s.name}</option>
              ))}
            </select>
            </>
          )}
          {onToggleTemplateCenter && (
            <button
              onClick={onToggleTemplateCenter}
              style={{
                padding: '6px 12px',
                border: '1px solid #1a2e05',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: '#1a2e05',
                color: '#fff',
              }}
            >
              {isTemplateCenter ? t.workspace.editNotes : t.templateCenter.title}
            </button>
          )}
        </div>
        {/* 右侧：自动保存 → 保存 → 重置 → 原始版/编辑版切换 → 导出Word */}
        <div className="preview-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {viewMode === 'edited' && (
            <>
              {lastSaveTime && (
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {lastSaveTime} {t.workspace.savedStatus}
                </span>
              )}
              <button
                onClick={handleSave}
                title={t.workspace.saveEdits}
                style={{
                  padding: '6px 12px',
                  border: justSaved ? '1px solid #10b981' : '1px solid #e5e7eb',
                  background: justSaved ? '#10b981' : '#fff',
                  color: justSaved ? '#fff' : '#374151',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.25s',
                }}
              >
                {justSaved ? t.workspace.savedCheck : t.workspace.save}
              </button>
              <button onClick={handleReset} title={t.workspace.reset} style={{ padding: '6px 12px', border: '1px solid #e5e7eb', background: '#fff', color: '#374151', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                {t.workspace.reset}
              </button>
            </>
          )}
          {/* 原始版/编辑版切换 */}
          <div style={{
            display: 'inline-flex',
            background: '#f3f4f6',
            borderRadius: '6px',
            padding: '2px',
            fontSize: '12px',
          }}>
            <button
              onClick={switchToOriginal}
              style={{
                padding: '4px 12px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                background: viewMode === 'original' ? '#fff' : 'transparent',
                color: viewMode === 'original' ? '#111827' : '#6b7280',
                fontWeight: viewMode === 'original' ? 500 : 400,
                boxShadow: viewMode === 'original' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {t.workspace.original}
            </button>
            <button
              onClick={switchToEdited}
              style={{
                padding: '4px 12px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                background: viewMode === 'edited' ? '#fff' : 'transparent',
                color: viewMode === 'edited' ? '#111827' : '#6b7280',
                fontWeight: viewMode === 'edited' ? 500 : 400,
                boxShadow: viewMode === 'edited' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {t.workspace.edited}
            </button>
          </div>
          <button
            onClick={handleExportToWord}
            disabled={isExporting}
            title={t.workspace.exportWordTitle}
            style={{ padding: '6px 12px', border: '1px solid #1a2e05', background: '#1a2e05', color: '#fff', borderRadius: '6px', fontSize: '12px', cursor: isExporting ? 'not-allowed' : 'pointer' }}
          >
            {isExporting ? t.workspace.exporting : t.workspace.exportWord}
          </button>
        </div>
      </div>
      )}

      {parseError && (
        <div style={{ padding: '8px 16px', background: '#fef2f2', borderBottom: '1px solid #fecaca', color: '#dc2626', fontSize: '13px', flexShrink: 0 }}>{parseError}</div>
      )}

      {/* 预览渲染区 - 可滚动 */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: '#fff' }}>
        {viewMode === 'edited' && !isSchemaEditMode ? (
          /* 编辑版布局：[黑色教学区 | 拖拽按钮区+Word内容区] */
          <div style={{ position: 'relative', minHeight: '100%' }}>
            {/* 教学引导区 - 绝对定位不占布局空间 */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 10, height: '100%' }}>
              <TutorialZone />
            </div>
            {/* 拖拽按钮区 + Word 内容区 + 连线按钮区 */}
            <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center' }} data-is-background="true">
              {/* Word 默认 A4: 拖拽按钮(24px) + 可用宽度 + 连线按钮(24px) */}
              <div style={{ width: `${WORD_CONTENT_WIDTH_PX + 24 + 24}px`, maxWidth: '100%' }}>
                <BlockEditorContainer
                  initialBlocks={stableEdited}
                  onBlocksChange={handleBlocksChange}
                  scopeClass={scopeClass}
                  css={css}
                  levelStyles={levelStyles}
                  activeLinkId={activeLinkId}
                  linkedIds={linkedIds}
                  onLinkToggle={onLinkToggle}
                  matchedNoteIds={matchedNoteIds}
                  topLinkedId={topLinkedId}
                />
              </div>
            </div>
          </div>
        ) : (
          /* 原始版/模板中心编辑版布局：居中显示，与 Word 一致 */
          <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: `${WORD_CONTENT_WIDTH_PX + 24 + 24}px`, maxWidth: '100%', paddingLeft: '24px' }}>
              {(() => {
                const displayBlocks = showDemoText ? demoBlocks : showOriginal ? realOriginal : (isSchemaEditMode ? editedBlocks : stableOriginal);
                const blockLevelIndices = computeBlockLevelIndices(displayBlocks);
                return (
                  <div className="flex flex-col" style={{ userSelect: 'text', cursor: 'default' }}>
                    {displayBlocks.map((block, index) => {
                      const levelIdx = block.level - 1;
                      const computed = levelStyles[levelIdx];
                      const styleConfig = originalStyleConfigs[levelIdx] || originalStyleConfigs[0];
                      const bulletText = computed
                        ? computeBulletText(computed, blockLevelIndices[index].index, blockLevelIndices[index].counters, levelStyles)
                        : '';
                      const isBlockLinked = linkedIds?.has(block.id);

                      return (
                        <div
                          key={block.id || index}
                          data-block-id={block.id}
                          className="preview-block-row"
                          style={{ ...styleConfig?.outerContainer, display: 'flex', alignItems: 'flex-start' }}
                        >
                          <div style={{ ...styleConfig?.contentWrapper, flex: 1, minWidth: 0 }}>
                            <div
                              data-selection-wrapper
                              style={{
                                ...styleConfig?.selectionWrapper,
                                borderRadius: '4px',
                                padding: isBlockLinked ? '2px 6px' : undefined,
                                ...(isBlockLinked ? {
                                  backgroundColor: 'rgba(232, 237, 223, 0.5)',
                                  boxShadow: 'inset 0 0 0 1px #4d7c0f',
                                } : {}),
                              }}
                            >
                              {bulletText && (
                                <span style={styleConfig?.bullet}>{bulletText}</span>
                              )}
                              <span style={styleConfig?.content}>{block.content}</span>
                            </div>
                          </div>
                          <div style={{ width: '24px', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: styleConfig?.outerContainer?.marginTop || '0px' }}>
                            {matchedNoteIds?.has(block.id) && onLinkToggle && (
                              <button
                                onClick={() => onLinkToggle(block.id)}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: isBlockLinked ? '2px solid white' : '2px solid #E8EDDF',
                                  background: isBlockLinked ? '#1a2e05' : 'white',
                                  color: isBlockLinked ? 'white' : '#a89f91',
                                  cursor: 'pointer',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  padding: 0,
                                }}
                                className={`block-link-btn-static${isBlockLinked ? ' linked-active' : ''}`}
                                title={t.workspace.viewLinkedSource}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EnhancedPreview;
